import dayjs from 'dayjs';
import localData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isDateObject } from './is';

/**
 * check if date value is after now
 * @param {mixed} d date value. object or string
 * @returns {boolean}
 */
const isDateAfterNow = (d) => ((d) ? dayjs(d).isAfter(dayjs()) : false);

/**
 * format date into string
 * @param {mixed} d date value. object or string
 * @param {boolean} withTime includes time in result
 * @returns {string}
 */
const formatDateToString = (d = '', withTime = false) => {
  if (!d) return '';
  dayjs.extend(localData);
  let format = 'DD/MM/YYYY';
  if (withTime) {
    format += ', HH:mm';
  }

  const formatted = dayjs(d).format(format);

  return formatted;
};

const formatDate = (d = new Date()) => d.toLocaleDateString('en-GB');

dayjs.extend(customParseFormat);
const isValidDate = (val) => dayjs(val, 'DD/MM/YYYY', true).isValid();

const stringToDate = (val = '') => {
  try {
    return isValidDate(val)
      ? dayjs(val, 'DD/MM/YYYY', true).toDate() // Enforcing strict parsing
      : null;
  } catch (err) {
    return null;
  }
};

const yearsToMonths = (years) => years * 12;

/**
 * get day name from date object
 * @returns {string}
 */
const getDayName = (d) => ((isDateObject(d)) ? d.toLocaleDateString('he-IL', { weekday: 'long' }) : null);

/**
 * Returns a Date object representing the last day of the month
 * that is a specified number of months ahead from the current month.
 *
 * @param {number} [num=2] - The number of months ahead from the current month.
 * @returns {Date} The last day of the target month.
 */
const getLastDayMonthAhead = (num = 2) => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() + num, 0);
};

/**
 * Get all dates between startMonth and endMonth that are not in slots
 * @param {Date[]} slots - array of available dates
 * @param {Date} startMonth - start date
 * @param {Date} endMonth - end date
 * @returns {Date[]} - array of unavailable dates
 */
function getUnavailableDates(slots, startMonth, endMonth) {
  // Convert slots to string format "YYYY-MM-DD" for easy comparison
  const slotStrings = new Set(slots.map((date) => date.toISOString().split('T')[0]));

  const unavailableDates = [];
  const currentDate = new Date(startMonth);

  while (currentDate <= endMonth) {
    const dateString = currentDate.toISOString().split('T')[0];
    if (!slotStrings.has(dateString)) {
      unavailableDates.push(new Date(currentDate));
    }
    currentDate.setDate(currentDate.getDate() + 1); // go to next day
  }

  return unavailableDates;
}

/**
 * Get full Date objects for start and end time
 * @param {Date} date - The base date (e.g. 2025-10-11)
 * @param {string} interval - Time string in "HH:MM" format (e.g. "01:30")
 * @param {string} startTime - Time string in "HH:MM:SS" format (e.g. "15:00:00")
 * @returns {{ startDate: Date, endDate: Date }}
 */
function getStartAndEndTime(date, interval, startTime) {
  const [startHours, startMinutes, startSeconds] = startTime.split(':').map(Number);
  const [intervalHours, intervalMinutes] = interval.split(':').map(Number);

  // create full start Date based on the given date
  const startDate = new Date(date);
  startDate.setHours(startHours, startMinutes, startSeconds || 0, 0);

  // clone and add the interval
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + intervalHours);
  endDate.setMinutes(endDate.getMinutes() + intervalMinutes);

  return { startDate, endDate };
}

/**
 * Formats a Date object into a string with hours and minutes in "HH:mm" format.
 *
 * @param {Date} date - The date object to format.
 * @returns {string} The formatted time string in "HH:mm" format.
 */
const formatTime = (date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

export {
  isDateAfterNow,
  formatDateToString,
  formatDate,
  stringToDate,
  yearsToMonths,
  getDayName,
  getLastDayMonthAhead,
  getUnavailableDates,
  getStartAndEndTime,
  formatTime,
};
